import React, { useMemo, useState } from "react";
import {
  Grid, Paper, Typography, Box, Stack, TextField, Button, Table, TableHead,
  TableRow, TableCell, TableBody, Chip, IconButton, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";

function Card({ title, subtitle, children }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid var(--border)", bgcolor: "var(--branco)" }}>
      <Typography sx={{ fontWeight: 900, color: "var(--cise-text)" }}>{title}</Typography>
      {subtitle && <Typography sx={{ color: "var(--cise-text-muted)", fontSize: 13, mt: 0.3 }}>{subtitle}</Typography>}
      <Box sx={{ mt: 2 }}>{children}</Box>
    </Paper>
  );
}

const MOCK_USERS = [
  { id: "u1", name: "SDR 1", email: "sdr1@cise.com.br", role: "SDR", active: true },
  { id: "u2", name: "SDR 2", email: "sdr2@cise.com.br", role: "SDR", active: true },
  { id: "u3", name: "SDR 3", email: "sdr3@cise.com.br", role: "SDR", active: false },
  { id: "u4", name: "Gestor", email: "gestor@cise.com.br", role: "Gestor", active: true },
  { id: "u5", name: "Admin", email: "admin@cise.com.br", role: "Admin", active: true },
];

function isValidPassword(password) {
  return /^(?=.*[A-Za-z])(?=.*\d).{6,}$/.test(password);
}

export default function UsersTab() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(MOCK_USERS);

  // dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "SDR",
    active: true,
  });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term) ||
      u.role.toLowerCase().includes(term)
    );
  }, [q, users]);

  const toggleActive = (id) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === id ? { ...u, active: !u.active } : u))
    );
  };

  function handleAddUser() {
    if (!form.name || !form.email || !form.password) {
      alert("Preencha todos os campos obrigatórios");
      return;
    }

    if (!isValidPassword(form.password)) {
      alert("A senha deve ter no mínimo 6 caracteres, com letra e número.");
      return;
    }

    setUsers((prev) => [
      ...prev,
      {
        id: `u${Date.now()}`,
        name: form.name,
        email: form.email,
        role: form.role,
        active: form.active,
        // password NÃO exibimos na tabela (futuro: hash/Firebase)
      },
    ]);

    setForm({
      name: "",
      email: "",
      password: "",
      role: "SDR",
      active: true,
    });

    setOpenDialog(false);
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card title="Usuários" subtitle="Gerencie acessos e status">
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                value={q}
                onChange={(e) => setQ(e.target.value)}
                size="small"
                fullWidth
                placeholder="Buscar por nome, e-mail, perfil..."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 999, background: "var(--bg)" } }}
              />

              <Button
                variant="contained"
                startIcon={<PersonAddAltOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: "var(--cise-blue)",
                  "&:hover": { bgcolor: "var(--cise-blue)" },
                  paddingLeft: 4,
                  paddingRight: 4,
                }}
                onClick={() => setOpenDialog(true)}
              >
                Adicionar
              </Button>
            </Stack>

            <Paper elevation={0} sx={{ border: "1px solid var(--border)", borderRadius: 2, overflow: "hidden" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "var(--bg)" }}>
                    <TableCell sx={{ fontWeight: 900 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>E-mail</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Perfil</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 900 }} align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered.map((u) => (
                    <UserRow key={u.id} user={u} onToggle={() => toggleActive(u.id)} />
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card title="Permissões" subtitle="Regras recomendadas do CRM">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Chip label="Admin: tudo" sx={{ fontWeight: 900, border: "1px solid var(--border)", bgcolor: "var(--branco)" }} />
              <Chip label="Gestor: relatórios, metas, usuários (sem deletar)" sx={{ fontWeight: 900, border: "1px solid var(--border)", bgcolor: "var(--branco)" }} />
              <Chip label="SDR: leads/conversas/agenda/tarefas" sx={{ fontWeight: 900, border: "1px solid var(--border)", bgcolor: "var(--branco)" }} />
              <Chip label="Leitura: somente visualizar" sx={{ fontWeight: 900, border: "1px solid var(--border)", bgcolor: "var(--branco)" }} />
            </Box>

            <Typography sx={{ mt: 2, color: "var(--cise-text-muted)", fontSize: 13 }}>
              Depois a gente transforma isso em regras reais (Firebase/Auth).
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* DIALOG ADICIONAR USUÁRIO */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Adicionar usuário</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              fullWidth
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <TextField
              label="E-mail"
              fullWidth
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />

            <TextField
              label="Senha"
              type="password"
              fullWidth
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              helperText="Mín. 6 caracteres, com letra e número"
              error={form.password.length > 0 && !isValidPassword(form.password)}
            />

            <FormControl fullWidth>
              <InputLabel>Perfil</InputLabel>
              <Select
                label="Perfil"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Gestor">Gestor</MenuItem>
                <MenuItem value="SDR">SDR</MenuItem>
                <MenuItem value="Leitura">Leitura</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.active ? "ativo" : "inativo"}
                onChange={(e) => setForm({ ...form, active: e.target.value === "ativo" })}
              >
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            sx={{ fontWeight: 900, bgcolor: "var(--cise-blue)" }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function UserRow({ user, onToggle }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 900 }}>{user.name}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        <Chip label={user.role} size="small" sx={{ fontWeight: 900, border: "1px solid var(--border)", bgcolor: "var(--branco)" }} />
      </TableCell>
      <TableCell>
        <Chip
          label={user.active ? "Ativo" : "Inativo"}
          color={user.active ? "success" : "default"}
          size="small"
          sx={{ fontWeight: 900 }}
        />
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>

        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { borderRadius: 2 } }}>
          <MenuItem onClick={() => { setAnchorEl(null); onToggle(); }}>
            {user.active ? "Desativar" : "Ativar"}
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); alert("Editar perfil (futuro)"); }}>
            Alterar perfil
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); alert("Resetar senha (futuro)"); }}>
            Resetar senha
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}